import { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart3, Bell, Mail, Lock, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROUTE_LABEL = {
  '/work-orders': 'WO',
  '/assets': 'Assets',
  '/inspections': 'Inspect',
  '/operations': 'Ops',
  '/vendors': 'Vendors',
  '/ppm': 'PPM',
  '/budget': 'Budget',
};

/**
 * Admin-only Dashboard tile surfacing org-wide most-applied saved views.
 * Hidden for non-admins. Powered by GET /api/admin/saved-views/usage.
 */
const SavedViewsAdminUsageWidget = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/saved-views/usage?limit=10`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        logger.debug('admin usage fetch failed', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  if (!isAdmin) return null;

  return (
    <Card className="card-dashboard h-full" data-testid="admin-saved-views-usage-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[#ff6600]" />
          Org saved-view usage
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 rounded-md bg-muted/50 animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">
            No usage yet — once anyone applies a view, leaderboards appear here.
          </p>
        ) : (
          <div className="space-y-1">
            {items.map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-muted/40 transition"
                data-testid={`admin-usage-row-${it.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {it.shared
                      ? <Users className="h-3 w-3 text-green-600 flex-shrink-0" title="Shared" />
                      : <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" title="Private" />}
                    <span className="text-sm font-medium truncate">{it.name}</span>
                    {it.scheduled && (
                      <Mail className="h-3 w-3 text-[#111565] flex-shrink-0" title="Scheduled email" />
                    )}
                    {it.subscriber_count > 0 && (
                      <Bell className="h-3 w-3 text-blue-500 flex-shrink-0" title={`${it.subscriber_count} subscriber${it.subscriber_count === 1 ? '' : 's'}`} />
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {ROUTE_LABEL[it.path] || it.path} · {it.owner_name}
                  </p>
                </div>
                <Badge variant="secondary" className="text-[11px] flex-shrink-0">
                  {it.access_count}×
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SavedViewsAdminUsageWidget;
